/**
 * ===================================================================
 * FRONTEND HOOKS - TASKS
 * ===================================================================
 * React Query hooks pro správu úkolů s podporou opakování.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../services/api';

export type TaskStatus = 'pending' | 'done' | 'cancelled';
export type TaskPriority = 'low' | 'medium' | 'high';
export type RecurrenceType = 'daily' | 'weekly' | 'monthly';

export interface Task {
  id: string;
  user_id: number;
  title: string;
  description?: string;
  due_date?: string;
  status: TaskStatus;
  priority: TaskPriority;
  related_entity_type?: string;
  related_entity_id?: string;
  recurrence_type?: RecurrenceType;
  recurrence_days?: number[];
  recurrence_end_date?: string;
  parent_task_id?: string;
  created_at: string;
  updated_at: string;
}

export interface CreateTaskInput {
  title: string;
  description?: string;
  due_date?: string;
  priority?: TaskPriority;
  related_entity_type?: string;
  related_entity_id?: string;
  recurrence_type?: RecurrenceType;
  recurrence_days?: number[];
  recurrence_end_date?: string;
}

export interface UpdateTaskInput {
  title?: string;
  description?: string;
  due_date?: string;
  status?: TaskStatus;
  priority?: TaskPriority;
  recurrence_type?: RecurrenceType | null;
  recurrence_days?: number[] | null;
  recurrence_end_date?: string | null;
}

export interface TaskQueryParams {
  status?: TaskStatus;
  priority?: TaskPriority;
  related_entity_type?: string;
  related_entity_id?: string;
  from?: string;
  to?: string;
}

// API functions
const taskApi = {
  getAll: async (params?: TaskQueryParams): Promise<Task[]> => {
    const queryParams = new URLSearchParams();
    if (params?.status) queryParams.append('status', params.status);
    if (params?.priority) queryParams.append('priority', params.priority);
    if (params?.related_entity_type)
      queryParams.append('related_entity_type', params.related_entity_type);
    if (params?.related_entity_id)
      queryParams.append('related_entity_id', params.related_entity_id);
    if (params?.from) queryParams.append('from', params.from);
    if (params?.to) queryParams.append('to', params.to);

    const url = `/tasks${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    const result = await apiClient.get<any>(url);
    // Support both raw array and {success, data} wrapper
    return result?.data || result || [];
  },

  getById: async (id: string): Promise<Task> => {
    return apiClient.get(`/tasks/${id}`);
  },

  create: async (input: CreateTaskInput): Promise<Task> => {
    return apiClient.post('/tasks', input);
  },

  update: async (id: string, input: UpdateTaskInput): Promise<Task> => {
    return apiClient.patch(`/tasks/${id}`, input);
  },

  markAsDone: async (id: string): Promise<Task> => {
    return apiClient.patch(`/tasks/${id}/done`, {});
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/tasks/${id}`);
  },
};

// Query Keys
export const taskKeys = {
  all: ['tasks'] as const,
  lists: () => [...taskKeys.all, 'list'] as const,
  list: (params?: TaskQueryParams) => [...taskKeys.lists(), params] as const,
  details: () => [...taskKeys.all, 'detail'] as const,
  detail: (id: string) => [...taskKeys.details(), id] as const,
};

// Hooks
export function useTasks(params?: TaskQueryParams) {
  return useQuery({
    queryKey: taskKeys.list(params),
    queryFn: () => taskApi.getAll(params),
  });
}

export function useTask(id: string) {
  return useQuery({
    queryKey: taskKeys.detail(id),
    queryFn: () => taskApi.getById(id),
    enabled: !!id,
  });
}

export function useCreateTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: taskApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: taskKeys.lists() });
    },
  });
}

export function useUpdateTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateTaskInput }) =>
      taskApi.update(id, input),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: taskKeys.lists() });
      queryClient.invalidateQueries({ queryKey: taskKeys.detail(variables.id) });
    },
  });
}

export function useMarkTaskDone() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: taskApi.markAsDone,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: taskKeys.lists() });
    },
  });
}

export function useDeleteTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: taskApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: taskKeys.lists() });
    },
  });
}

// Utility: Get tasks for a specific batch
export function useBatchTasks(batchId: string) {
  return useTasks({
    related_entity_type: 'batch',
    related_entity_id: batchId,
  });
}

// Utility: Get pending tasks only
export function usePendingTasks() {
  return useTasks({ status: 'pending' });
}
